import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _PropTypes from 'prop-types';
import { rgba } from 'polished';
import * as React from 'react';
import styled from 'styled-components';
import { neutralColors } from '../../styles/colors/colors.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { Circle } from '../Circle/Circle.js';
import { IconEdit } from '../Icons/IconEdit.js';
import { IconUser } from '../Icons/IconUser.js';
import { Tooltip } from '../Tooltip/Tooltip.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var avatarSizeToPixelsMap = {
  lg: {
    fontSize: 18,
    iconSize: 24
  },
  md: {
    fontSize: 16,
    iconSize: 20
  },
  sm: {
    fontSize: 14,
    iconSize: 14
  },
  xs: {
    fontSize: 10,
    iconSize: 10
  }
};

var getInitials = function getInitials(name) {
  var splitName = name.split(" ");
  var firstName = splitName[0];
  var lastName = splitName[splitName.length - 1];
  return splitName.length > 1 ? firstName[0] + lastName[0] : firstName[0];
};

var IconEditContainer = styled.div.withConfig({
  displayName: "AvatarUser__IconEditContainer",
  componentId: "m6y2qs-0"
})(["&&{width:100%;height:100%;position:absolute;left:0;top:0;display:none;align-items:center;justify-content:center;}"]);
var StyledCircle = styled(Circle).withConfig({
  displayName: "AvatarUser__StyledCircle",
  componentId: "m6y2qs-1"
})(["&&{font-weight:normal;text-transform:uppercase;position:relative;", ";", ";", ";}&&:hover ", ",&&:focus ", "{display:flex;background-color:", ";}"], function (props) {
  return "font-size: ".concat(avatarSizeToPixelsMap[props.size].fontSize, "px;");
}, function (props) {
  return props.isUnassigned && " background-color: ".concat(neutralColors.rhino, ";");
}, function (props) {
  return props.showBorder && " border: 1px solid ".concat(neutralColors.silver, ";");
}, IconEditContainer, IconEditContainer, rgba(neutralColors.charcoal, 0.5));
var AvatarUser = /*#__PURE__*/React.forwardRef(function AvatarUser2(props, ref) {
  var name = props.name,
      onClick = props.onClick,
      src = props.src,
      tooltipProps = props.tooltipProps,
      imgProps = props.imgProps,
      _props$size = props.size,
      size = _props$size === void 0 ? "md" : _props$size,
      hideToolTip = props.hideToolTip,
      editable = props.editable;

  var _useThemeContext = useThemeContext(),
      i18n = _useThemeContext.i18n;

  var _i18n$AvatarUser = i18n.AvatarUser,
      editAvatar = _i18n$AvatarUser.editAvatar,
      userAvatarLabel = _i18n$AvatarUser.userAvatarLabel,
      unknownUserLabel = _i18n$AvatarUser.unknownUserLabel;
  var iconSize = avatarSizeToPixelsMap[size].iconSize;
  var circleContent;
  var tooltipText = name;

  if (src) {
    circleContent = /*#__PURE__*/jsx("img", _objectSpread({
      src: src,
      alt: name || userAvatarLabel,
      style: {
        height: "100%",
        width: "100%"
      }
    }, imgProps));
  } else if (name) {
    circleContent = /*#__PURE__*/jsx("span", {
      role: "img",
      "aria-label": name,
      children: /*#__PURE__*/jsx("span", {
        "aria-hidden": true,
        children: getInitials(name)
      })
    });
  } else {
    tooltipText = unknownUserLabel;
    circleContent = /*#__PURE__*/jsx(IconUser, {
      role: "img",
      "aria-hidden": false,
      focusable: true,
      height: iconSize,
      width: iconSize
    });
  }

  var userAvatar = /*#__PURE__*/jsxs(StyledCircle, {
    onClick: onClick,
    size: size,
    "aria-label": onClick ? editAvatar : void 0,
    as: onClick ? "button" : "div",
    ref: ref,
    isUnassigned: tooltipText === unknownUserLabel,
    showBorder: !!src,
    children: [circleContent, editable && onClick && /*#__PURE__*/jsx(IconEditContainer, {
      "aria-hidden": true,
      children: /*#__PURE__*/jsx(IconEdit, {
        height: iconSize,
        width: iconSize
      })
    })]
  });

  return hideToolTip || !(tooltipText || tooltipProps) ? userAvatar : /*#__PURE__*/jsx(Tooltip, _objectSpread(_objectSpread({
    content: tooltipText
  }, tooltipProps), {}, {
    children: userAvatar
  }));
});
AvatarUser.propTypes = {
  "editable": _PropTypes.bool,
  "hideToolTip": _PropTypes.bool,
  "imgProps": _PropTypes.object,
  "name": _PropTypes.string,
  "onClick": _PropTypes.func,
  "size": _PropTypes.oneOf(["lg", "md", "sm", "xs"]),
  "src": _PropTypes.string,
  "tooltipProps": _PropTypes.object
};
AvatarUser.displayName = "AvatarUser";

export { AvatarUser, avatarSizeToPixelsMap };
